import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Input, Space, Table, message } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import IconButton from "@mui/material/IconButton";
import { ExpandAltOutlined, SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import "./bonusesV2.css";

const apiUrl =
  "https://glzf596ea7.execute-api.us-east-1.amazonaws.com/dev/get_all_testers_bonuses";
const testerDetailsUrl =
  "https://glzf596ea7.execute-api.us-east-1.amazonaws.com/dev/get_tester_details";
const addBonusUrl =
  "https://glzf596ea7.execute-api.us-east-1.amazonaws.com/dev/add-bonus-for-tester";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BonusesV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [reload, setReload] = useState(false);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedTester, setSelectedTester] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialBonus, setInitialBonus] = useState("");
  const [editedBonus, setEditedBonus] = useState("");
  const [testerDetails, setTesterDetails] = useState(null);
  const [saving, setSaving] = useState(false);
  const searchInput = useRef(null);

  const isBonusChanged = useMemo(() => initialBonus !== editedBonus, [initialBonus, editedBonus]);

  const handleCloseModal = () => {
    setOpen(false);
    setTesterDetails(null);
    setEditedBonus("");
    setInitialBonus("");
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleViewClick = async (record) => {
    setSelectedTester(record);
    setOpen(true);
    try {
      const res = await axios.get(`${testerDetailsUrl}/${record.user_id}`);
      if (res.status === 200 && res.data.body) {
        const data = JSON.parse(res.data.body);
        setTesterDetails(data);
        setInitialBonus(data.current_month_bonus ?? "");
        setEditedBonus(data.current_month_bonus ?? "");
      } else {
        messageApi.error("Failed to fetch tester details");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Something went wrong while fetching details");
    }
  };

  const calculatePayment = (count) => {
    const payment = (count || 0) * 0.01;
    return payment.toFixed(3);
  };

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch data");
      const result = await response.json();
      const parsedData = JSON.parse(result.body);
      const data = parsedData.data || [];
      const testersWithPayment = data.map((tester) => ({
        ...tester,
        payment: calculatePayment(tester.previous_month_count),
      }));
      setTesters(testersWithPayment);
    } catch (error) {
      console.error("Error fetching bonuses:", error);
      messageApi.error("Failed to fetch bonuses");
      setTesters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    const downloadData = sortedUsers.map((item) => ({
      First_Name: item.first_name,
      Last_Name: item.last_name,
      Email: item.email,
      Current_Month_Count: item.current_month_count,
      Previous_Month_Count: item.previous_month_count,
      Bonus: item.payment,
    }));
    const csvData = Papa.unparse(downloadData);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "Bonuses.csv");
  };

  const handleSaveBonus = async () => {
    if (!isBonusChanged || !testerDetails) return;
    setSaving(true);
    try {
      const response = await axios.post(addBonusUrl, {
        tester_id: testerDetails.id,
        bonus_amount: editedBonus,
      });
      if (response.data?.statusCode !== 200) {
        messageApi.error("Failed to update bonus");
      } else {
        messageApi.success("Bonus updated successfully");
        handleCloseModal();
        setReload((r) => !r);
      }
    } catch (error) {
      messageApi.error("Failed to update bonus");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [reload]);

  const sortedUsers = useMemo(() => {
    return [...testers].sort(
      (a, b) => (b.current_month_count || 0) - (a.current_month_count || 0)
    );
  }, [testers]);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="bonuses-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="bonuses-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "bonuses-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Tester Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "14%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "18%",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Current Month Count",
      dataIndex: "current_month_count",
      key: "current_month_count",
      width: "12%",
      sorter: (a, b) => (a.current_month_count || 0) - (b.current_month_count || 0),
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "Previous Month Count",
      dataIndex: "previous_month_count",
      key: "previous_month_count",
      width: "14%",
      sorter: (a, b) => (a.previous_month_count || 0) - (b.previous_month_count || 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Bonus (USD)",
      dataIndex: "current_month_bonus",
      key: "current_month_bonus",
      width: "10%",
      sorter: (a, b) =>
        parseFloat(a.current_month_bonus || 0) - parseFloat(b.current_month_bonus || 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "14%",
      render: (_, record) => (
        <Button
          type="default"
          icon={<ExpandAltOutlined />}
          onClick={() => handleViewClick(record)}
          className="bonuses-v2-btn-action"
        >
          Add Bonus
        </Button>
      ),
    },
  ];

  return (
    <div className="bonuses-v2">
      {contextHolder}
      <div className="bonuses-v2-header">
        <h1 className="bonuses-v2-title">Bonuses for All Testers</h1>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadCsv}
          className="bonuses-v2-download-btn"
        >
          Download CSV
        </Button>
      </div>

      <div className="bonuses-v2-card">
        <Table
          columns={columns}
          dataSource={sortedUsers}
          rowKey={(r, idx) => r.user_id || r.id || `${r.first_name || ""}-${r.email || ""}-${idx}`}
          loading={loading}
          locale={{ emptyText: loading ? "Loading..." : "No data" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} testers`,
          }}
          className="bonuses-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Dialog
        open={open}
        onClose={handleCloseModal}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="bonuses-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          },
        }}
      >
        <AppBar className="bonuses-v2-appbar" position="relative">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flex: 1 }}>
              Add Bonus
            </Typography>
            {testerDetails && (
              <Typography variant="body2" sx={{ mr: 1, opacity: 0.95 }}>
                {testerDetails.first_name} {testerDetails.last_name}
              </Typography>
            )}
            <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box className="bonuses-v2-modal-body">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <label className="bonuses-v2-field-label">First Name</label>
              <Input
                value={testerDetails?.first_name || ""}
                disabled
                className="bonuses-v2-input"
              />
            </Grid>
            <Grid item xs={6}>
              <label className="bonuses-v2-field-label">Last Name</label>
              <Input
                value={testerDetails?.last_name || ""}
                disabled
                className="bonuses-v2-input"
              />
            </Grid>
            <Grid item xs={12}>
              <label className="bonuses-v2-field-label">Email</label>
              <Input
                value={testerDetails?.email || ""}
                disabled
                className="bonuses-v2-input"
              />
            </Grid>
            <Grid item xs={6}>
              <label className="bonuses-v2-field-label">Current Month Count</label>
              <Input
                value={testerDetails?.current_month_count ?? ""}
                disabled
                className="bonuses-v2-input"
              />
            </Grid>
            <Grid item xs={6}>
              <label className="bonuses-v2-field-label">Previous Month Count</label>
              <Input
                value={testerDetails?.previous_month_count ?? ""}
                disabled
                className="bonuses-v2-input"
              />
            </Grid>
            <Grid item xs={12}>
              <label className="bonuses-v2-field-label">Bonus (USD)</label>
              <Input
                value={editedBonus}
                onChange={(e) => setEditedBonus(e.target.value)}
                className="bonuses-v2-input"
                type="number"
                step="0.01"
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="bonuses-v2-modal-footer">
          <Button
            type="primary"
            disabled={!isBonusChanged || saving}
            loading={saving}
            onClick={handleSaveBonus}
            className="bonuses-v2-save-btn"
          >
            Save
          </Button>
        </Box>
      </Dialog>
    </div>
  );
};

export default BonusesV2;
